import React, { useEffect, useRef, useState } from 'react';
import type LottieWeb from 'lottie-web';
import type { AnimationItem, AnimationSegment } from 'lottie-web';
import { loadable } from '@shared/core/loadableFromNamedExport';
import { withWindow } from '@shared/utils/withWindow';

export interface LottiePlayerProps {
  loop?: boolean;
  autoplay?: boolean;
  animationProgress?: number;
  restart?: boolean;
  lottieUrl: string;
  play?: boolean;
  delay?: number;
  playSegment?: AnimationSegment;
  containerStyle?: React.CSSProperties;
  /** Fired at the end of each loop cycle (lottie-web's `loopComplete` event). */
  onLoopComplete?: () => void;
}

const Lottie = loadable.lib(
  () =>
    // Removing the magic webpackChunkName because it's interfering with
    // webpack's internal chunking algorithm.
    import('lottie-web'),
  {
    ssr: false,
  },
);

const Root: React.FC<LottiePlayerProps & { lottie: typeof LottieWeb }> = ({
  loop = false,
  autoplay = false,
  animationProgress = 0,
  restart,
  lottieUrl,
  play,
  lottie,
  delay,
  playSegment,
  containerStyle,
  onLoopComplete,
}) => {
  const animationContainer = useRef<HTMLDivElement>(null);
  const [animation, setAnimation] = useState<AnimationItem | null>(null);

  useEffect(() => {
    if (!animation && animationContainer.current) {
      setAnimation(
        lottie.loadAnimation({
          container: animationContainer.current,
          renderer: 'svg',
          loop,
          autoplay,
          path: lottieUrl,
          rendererSettings: {
            progressiveLoad: false,
            preserveAspectRatio: 'xMidYMid slice',
          },
        }),
      );
    }
    return () => animation?.destroy();
  }, [animationContainer, animation, autoplay, loop, lottieUrl, lottie]);

  useEffect(() => {
    let animationDelay: number;
    if (animation) {
      if (play) {
        if (delay) {
          withWindow(window => {
            animationDelay = window.setTimeout(() => {
              if (playSegment) {
                animation.playSegments(playSegment, true);
              } else {
                animation.play();
              }
            }, delay);
          });
        } else if (playSegment) {
          animation.playSegments(playSegment, true);
        } else {
          animation.play();
        }
      } else if (play === false) {
        animation.pause();
      }
      if (animationProgress) {
        const totalFrames = animation.getDuration(true) - 1;
        const currentFrame = Math.round(animationProgress * totalFrames);
        animation.goToAndStop(currentFrame, true);
      }
    }

    return () => {
      withWindow(window => {
        window.clearTimeout(animationDelay);
      });
    };
  }, [animation, animationProgress, play, delay, playSegment]);

  useEffect(() => {
    if (animation && restart) {
      animation.goToAndPlay(1, true);
    }
  }, [restart, animation]);

  useEffect(() => {
    if (!animation || !onLoopComplete) return;
    animation.addEventListener('loopComplete', onLoopComplete);
    return () => animation.removeEventListener('loopComplete', onLoopComplete);
  }, [animation, onLoopComplete]);

  return (
    <div
      ref={animationContainer}
      style={containerStyle}
    />
  );
};

export const LottiePlayer: React.FC<LottiePlayerProps> = props => {
  return (
    <Lottie fallback={<div></div>}>
      {({ default: lottie }) => (
        <Root
          {...props}
          lottie={lottie}
        />
      )}
    </Lottie>
  );
};
